/**
 * Profile picture: change it or remove it.
 *
 * The flow is deliberately hybrid:
 *
 *   1. The BINARY goes over REST (`POST /api/avatar`) — it doesn't fit the
 *      sync queue, and the server has to normalize it (crop, WebP, no EXIF).
 *   2. The KEY it returns is written to the local row with Drizzle, just
 *      like language, font and theme. It syncs up on its own via the write-path.
 *
 * Practical consequence: the photo shows up instantly, without waiting for
 * the session to refresh.
 *
 * FLAGS: the section hides with `academy.profile.avatar` off, unless there
 * is already a photo — in which case the REMOVE button keeps showing. A
 * product switch cannot leave anyone unable to delete their own photograph.
 */
import { msg } from "@lingui/core/macro";
import { type Accessor, createSignal, Show } from "solid-js";
import { Camera, Loader2, Trash2 } from "@fleet/icons";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import { PROFILE_AVATAR_KEY, updateUserImageMutation } from "@space/shared";

import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { deleteAvatar, uploadAvatar } from "@/lib/api";
import { prepareAvatarFile, validateAvatarFile } from "@/lib/avatar-file";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useI18n } from "@/providers/i18n-provider";
import { useUserPreferences } from "@/providers/user-preferences-context";

interface AvatarSectionProps {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  userId: Accessor<string | null>;
  name: Accessor<string | null | undefined>;
  email: Accessor<string>;
  /**
   * The photo the SESSION carries. Only the starting value: the local row
   * takes precedence (see `image()` below), because the session comes from
   * the API and stays cached.
   */
  image: Accessor<string | null | undefined>;
}

export function AvatarSection(props: AvatarSectionProps) {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const { preferences } = useUserPreferences();
  // `platformOnly`: the server's gate (`handlers::avatar`) is called with
  // `org_id = None` because the photo belongs to the USER, not an academy.
  // The client has to resolve the flag the same way, or an academy that
  // turned it on for itself would show the button and get a 403 on click.
  const avatarFeature = useFeatureEnabled(PROFILE_AVATAR_KEY, { platformOnly: true });

  /**
   * The EFFECTIVE photo.
   *
   * The local row takes precedence over the session: that's what makes a
   * freshly uploaded photo show up instantly (the session doesn't refresh
   * on every write). `undefined` = the row hasn't loaded yet, in which case
   * whatever the session says holds; `null` = the row says there is NO
   * photo, and that wins (the "remove the photo" case).
   */
  const image = () => {
    const local = preferences()?.image;
    return local === undefined ? props.image() : local;
  };
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  let inputRef: HTMLInputElement | undefined;

  /** Writes the key (or `null`) to the local row. */
  const writeLocal = async (key: string | null) => {
    const db = drizzle();
    const userId = props.userId();
    if (db && userId) {
      await updateUserImageMutation(db, userId, key);
    }
  };

  const handleFile = async (file: File) => {
    setError(null);

    const invalid = validateAvatarFile(file);
    if (invalid === "too-large") {
      setError(t(msg`That image is too large. Pick one under 5 MB.`));
      return;
    }
    if (invalid === "unsupported-type") {
      setError(t(msg`That file isn't an image we can use. Try a JPG, PNG or WebP.`));
      return;
    }

    setBusy(true);
    try {
      const prepared = await prepareAvatarFile(file);
      const { key } = await uploadAvatar(prepared);
      await writeLocal(key);
    } catch (e) {
      // `cause` carries the HTTP status (see `uploadAvatar`).
      const status = e instanceof Error ? e.cause : undefined;
      if (status === 403) {
        // Rule zero: this account can't have a visible photo. Explained
        // without blaming whoever tries, and without revealing the record
        // data behind it.
        setError(
          t(
            msg`This account can't set a profile picture. If you're a student, ask your academy to complete your record.`,
          ),
        );
      } else if (status === 429) {
        setError(t(msg`Too many changes in a row. Try again in a little while.`));
      } else if (status === 400) {
        setError(t(msg`We couldn't read that image. Try a different one.`));
      } else {
        setError(t(msg`We couldn't upload your picture. Check your connection and try again.`));
      }
    } finally {
      setBusy(false);
      // Allows re-picking the SAME file after a failure: without this the
      // input doesn't fire `change` the second time.
      if (inputRef) {
        inputRef.value = "";
      }
    }
  };

  const handleRemove = async () => {
    setError(null);
    setBusy(true);
    try {
      await deleteAvatar();
      await writeLocal(null);
    } catch {
      setError(t(msg`We couldn't remove your picture. Check your connection and try again.`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      class="flex flex-col items-center gap-4 sm:flex-row sm:items-center"
      data-testid="avatar-section"
    >
      <Avatar
        userId={props.userId()}
        name={props.name()}
        email={props.email()}
        image={image()}
        size="xl"
        alt={t(msg`Your profile picture`)}
        testId="avatar-section-preview"
      />

      <div class="flex flex-col items-center gap-2 sm:items-start">
        <div class="flex flex-wrap items-center justify-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            class="sr-only"
            data-testid="avatar-file-input"
            onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              if (file) {
                void handleFile(file);
              }
            }}
          />
          <Show when={avatarFeature().enabled}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy()}
              onClick={() => inputRef?.click()}
              data-testid="avatar-change-button"
            >
              <Show when={busy()} fallback={<Camera class="mr-2 h-4 w-4" />}>
                <Loader2 class="mr-2 h-4 w-4 animate-spin" />
              </Show>
              {image() ? t(msg`Change photo`) : t(msg`Add photo`)}
            </Button>
          </Show>

          <Show when={image()}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy()}
              onClick={() => void handleRemove()}
              data-testid="avatar-remove-button"
            >
              <Trash2 class="mr-2 h-4 w-4" />
              {t(msg`Remove`)}
            </Button>
          </Show>
        </div>

        <p class="text-xs text-muted-foreground">
          {t(msg`JPG, PNG or WebP, up to 5 MB. We crop it to a square.`)}
        </p>

        <Show when={error()}>
          {(message) => (
            <p class="text-xs text-destructive" role="alert" data-testid="avatar-error">
              {message()}
            </p>
          )}
        </Show>
      </div>
    </div>
  );
}
